import { Card, Col, Row, Statistic, Typography } from "antd";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { AppDispatch } from "../../../core/data/redux/store";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";
import { formatCurrency } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import Table from "../../../core/common/dataTable/index";

const CompensationOverview = () => {
  const { compensationData = [] } = useSelector((state: any) => state.dashboard.hrDashboard);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { employeesbyUser } = useSelector((state: any) => state.employee);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTableData, setSelectedTableData] = useState<any[]>([]);
  const [tableKey, setTableKey] = useState(0);

  const payrollColumns = [
    { title: "Employee ID", dataIndex: "employeeId" },
    { title: "Employee Name", dataIndex: "employeeName" },
    { title: "Department", dataIndex: "departmentName" },
    { title: "HR Name", dataIndex: "hrName" },
    { title: "Month", dataIndex: "monthName" },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      render: (t: any) => (t ? new Date(t).toLocaleDateString() : "")
    },
    { title: "Net Salary", dataIndex: "net_salary" }
  ];

  const handleCardClick = () => {
    const employeeData = compensationData?.employeePayrollMonthly || [];

    setSelectedCategory("Monthly Payroll Employees");
    setSelectedTableData(employeeData);
    setTableKey(prev => prev + 1);
  };

  const handleDepartmentClick = (entry: any) => {
    const department = entry.role; // from chart XAxis

    // filter employees by department
    const filtered = (compensationData?.employeePayrollMonthly || []).filter(
      (emp: any) => emp.departmentName === department
    );

    setSelectedCategory(`Department: ${department}`);
    setSelectedTableData(filtered);
    setTableKey((prev) => prev + 1);
  };



  useEffect(() => {
    if (!employeesbyUser?.employeeId && user?.UserId) {
      dispatch(getEmployeeByUserId({ userId: user.UserId }));
    }
  }, [dispatch, employeesbyUser, user]);

  const salaryBenchmarkData = useMemo(() => {
    return (compensationData?.departmentWiseSalary || []).map((d: any) => ({
      role: d.departmentName,
      avgPay: parseFloat(d.total_net_salary) || 0,
      // industryAvg: parseFloat(d.industryAvg || "0") || 0, // if API provides industry avg
    }));
  }, [compensationData?.departmentWiseSalary]);

  const config = useAppConfig();

  return (
    <>
      {/* KPI Cards */}
      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={handleCardClick} style={{ cursor: "pointer" }}>
            <Statistic
              title="Monthly Payroll"
              value={formatCurrency(compensationData?.hrwiseMonthlyPayroll[0]?.totalPayroll || "0", config)}
              valueStyle={{ color: "#1890ff" }}
              precision={0}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100">
            <Statistic
              title="Quarterly Payroll"
              value={formatCurrency(compensationData?.hrwiseQuarterlyPayroll[0]?.totalPayroll || "0", config)}
              valueStyle={{ color: "#52c41a" }}
              precision={0}
            />
          </Card>
        </Col>
        {selectedCategory && (
          <div className="card mt-4" style={{ width: "100%" }}>
            <div className="card-header">
              <h5 className="mb-0 text-capitalize">{selectedCategory}</h5>
            </div>

            <div className="card-body p-0" style={{ width: "100%" }}>
              <Table
                key={tableKey}
                dataSource={selectedTableData}
                columns={payrollColumns}
              />
            </div>
          </div>
        )}
      </Row>

      {/* Salary Benchmark Chart */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card title="Salary Benchmark">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salaryBenchmarkData}>
                <XAxis dataKey="role" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgPay" fill="#1890ff" name="Department Avg" onClick={handleDepartmentClick} />
                {/* <Bar dataKey="industryAvg" fill="#52c41a" name="Industry Avg" /> */}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CompensationOverview;
